import React, { useState } from "react";
import DiscordIcon from "../../../../assets/images/icon-discord.svg";
import XIcon from "../../../../assets/images/icon-x.svg";
import FacebookIcon from "../../../../assets/images/icon-facebook.svg";
import CommunityArt from "../../../../assets/images/community-art.png";
import styles from "./CommunitySection.scss";
import { useScrollAnimation } from "../hooks/useScrollAnimation";

export function CommunitySection() {
  // スクロールアニメーション
  const [holeRef, holeVisible] = useScrollAnimation({ threshold: 0.3 });
  const [guideRef, guideVisible] = useScrollAnimation({ threshold: 0.2 });
  const [charterRef, charterVisible] = useScrollAnimation({ threshold: 0.15 });
  const [stepsRef, stepsVisible] = useScrollAnimation({ threshold: 0.15 });
  const [activityRef, activityVisible] = useScrollAnimation({ threshold: 0.2 });

  // モバイル用アコーディオン状態
  const [openSections, setOpenSections] = useState({
    guide: false,
    charter: false,
    steps: false,
    activity: false
  });

  const toggleSection = section => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const charterItems = [
    {
      number: 1,
      title: "想いを尊重しよう。",
      description:
        "どんな小さな「やってみたい」も、ここでは大切な種です。批評よりも対話を、否定よりも共感を。互いの想いを尊重し合うことから共創は始まります。"
    },
    {
      number: 2,
      title: "自分から動こう。",
      description:
        "MetaCampUsに「観客」はいません。誰かを待つのではなく、自分から声をかけ、問いを立て、場を動かしていきましょう。"
    },
    {
      number: 3,
      title: "共に学び、創ろう。",
      description:
        "ここは知識を共有する場であり、試行錯誤を楽しむ場でもあります。完璧を求めず、実験する気持ちで一歩を踏み出してみましょう。"
    },
    {
      number: 4,
      title: "違いを楽しもう。",
      description:
        "専門や立場、地域や世代の違いこそが、新しい発想の源です。そこに上下はありません。異なる視点と出会うことを楽しみ、共に考える余白を大切にしましょう。"
    },
    {
      number: 5,
      title: "「次の誰か」へつなげよう。",
      description:
        "あなたの活動や学びが、次の仲間のきっかけになります。成果も失敗も共有しながら、次の誰かにバトンを渡していきましょう。"
    }
  ];

  const steps = [
    {
      step: 1,
      title: "Discordに参加する",
      description:
        "MetaCampUsのメインコミュニケーションツールはDiscordです。まずは下のリンクからサーバーに参加してください。\nもしよければ、自己紹介チャンネルで軽くご挨拶を。",
      buttonText: "Discordに参加する",
      buttonLink: "https://discord.gg/rexWj6BYgd",
      buttonIcon: DiscordIcon,
      buttonStyle: "discord"
    },
    {
      step: 2,
      title: "登録してエントランスワールドに入る",
      description:
        "次に、MetaCampUsのメタバース空間「エントランスワールド」へ。ブラウザでアクセスでき、特別なアプリのインストールは不要です。初めての方は、ナビゲーターが操作を案内します。",
      buttonText: "エントランスワールドへ",
      buttonLink: "https://metacampus.jp/gaYGQMU/エントランスワールド",
      buttonStyle: "primary"
    },
    {
      step: 3,
      title: "誰かを誘って使ってみる",
      description:
        "MetaCampUsは、一人で眺めるより、誰かと一緒に使うものです。興味の近い友人や同僚を誘って、エントランスを歩いてみたり、Discordで一緒に話してみたり。「こんなことできるかも」と思いついたら、それ自体が新しいラボやプロジェクトの種になります。",
      shareButtons: true
    }
  ];

  const activities = [
    "研究テーマをベースにしたディスカッション",
    "学生×社会人のプロジェクト立ち上げ",
    "地域課題やスタートアップ構想の共有会",
    "自由な雑談・偶発的な出会い"
  ];

  return (
    <section id="community" className={styles.communitySection}>
      {/* アート画像 - セクション基準で配置 */}
      <img
        src={CommunityArt}
        alt=""
        className={`${styles.holeArt} ${holeVisible ? styles.artAnimate : styles.artHidden}`}
      />
      {/* 不定形の緑のくぼみ穴 */}
      <div ref={holeRef} className={`${styles.holeContainer} ${holeVisible ? styles.holeAnimate : styles.holeHidden}`}>
        {/* アニメーションする穴 */}
        <svg className={styles.holeSvg} viewBox="-10 -10 120 120" preserveAspectRatio="none">
          <defs>
            <linearGradient id="communityHoleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#C4E4D2" />
              <stop offset="50%" stopColor="#BEDBE4" />
              <stop offset="100%" stopColor="#C4E4D2" />
            </linearGradient>
            <filter id="communityInnerShadow" x="-50%" y="-50%" width="200%" height="200%">
              <feFlood floodColor="black" floodOpacity="1" result="flood" />
              <feComposite in="flood" in2="SourceAlpha" operator="out" result="outside" />
              <feOffset in="outside" dx="3" dy="3" result="offsetShadow" />
              <feGaussianBlur in="offsetShadow" stdDeviation="4" result="blurShadow" />
              <feComposite in="blurShadow" in2="SourceAlpha" operator="in" result="innerShadow" />
              <feColorMatrix
                in="innerShadow"
                type="matrix"
                result="coloredShadow"
                values="0 0 0 0 0
                        0 0 0 0 0
                        0 0 0 0 0
                        0 0 0 0.5 0"
              />
              <feMerge>
                <feMergeNode in="SourceGraphic" />
                <feMergeNode in="coloredShadow" />
              </feMerge>
            </filter>
          </defs>
          <path fill="url(#communityHoleGradient)" filter="url(#communityInnerShadow)">
            <animate
              attributeName="d"
              dur="20s"
              repeatCount="indefinite"
              values="
                M 18 12 C 25 -2 42 -2 52 10 S 85 -2 100 18 S 118 48 102 68 S 118 105 92 118 S 48 128 32 105 S -2 115 2 68 S -8 28 18 12 Z;
                M 15 18 C 28 2 38 5 55 8 S 88 2 102 22 S 115 52 98 72 S 112 108 88 120 S 45 125 28 102 S 2 110 5 62 S -5 32 15 18 Z;
                M 20 8 C 32 -5 45 0 52 15 S 82 0 98 15 S 120 42 105 65 S 122 102 95 115 S 52 130 35 108 S -5 118 0 72 S -10 25 20 8 Z;
                M 18 12 C 25 -2 42 -2 52 10 S 85 -2 100 18 S 118 48 102 68 S 118 105 92 118 S 48 128 32 105 S -2 115 2 68 S -8 28 18 12 Z
              "
            />
          </path>
        </svg>
      </div>

      <div className={styles.communityContainer}>
        {/* セクションタイトル */}
        <div ref={guideRef} className={`${styles.headerSection} ${guideVisible ? styles.fadeInUp : styles.hidden}`}>
          <h2 className={styles.sectionTitle}>Community</h2>
          <p className={styles.sectionSubtitle}>
            MetaCampUsには、誰でも参加可能なオープンコミュニティ（Discord）があります。
            <br />
            ここでは、ひとつの大きな組織ではなく、無数の小さなコミュニティが共鳴し合うネットワークを目指しています。
            <br />
            もしかすると、あなたの想いが、新しいラボや活動の種になるかもしれません。
            <br />
            <br />
            ぜひコミュニティに参加し、興味や関心を重ねながら、新しいプロジェクトや場を一緒に生み出していきましょう。
          </p>
        </div>

        {/* 1. コミュニティ参加ガイド */}
        <div className={`${styles.accordionSection} ${guideVisible ? styles.fadeInUp : styles.hidden}`}>
          <button
            className={`${styles.accordionHeader} ${openSections.guide ? styles.accordionOpen : ""}`}
            onClick={() => toggleSection("guide")}
          >
            <h3 className={styles.accordionTitle}>コミュニティ参加ガイド</h3>
            <span className={styles.accordionIcon}></span>
          </button>
          <div className={`${styles.accordionContent} ${openSections.guide ? styles.accordionContentOpen : ""}`}>
            <div className={styles.accordionInner}>
              <h3 className={styles.desktopSectionTitle}>コミュニティ参加ガイド</h3>
              <p className={styles.guideDescription}>
                MetaCampUsは、想いを持つ人が出会い、語り、形にしていくオープンコミュニティです。
                <br />
                参加に特別な資格やスキルは必要ありません。必要なのは「やってみたい」という気持ちだけ。
                <br />
                ここから、あなたの旅が始まります。
              </p>
            </div>
          </div>
        </div>

        {/* 2. コミュニティ憲章 */}
        <div
          ref={charterRef}
          className={`${styles.accordionSection} ${styles.charterAccordion} ${
            charterVisible ? styles.fadeInUp : styles.hidden
          }`}
        >
          <button
            className={`${styles.accordionHeader} ${openSections.charter ? styles.accordionOpen : ""}`}
            onClick={() => toggleSection("charter")}
          >
            <h3 className={styles.accordionTitle}>コミュニティ憲章</h3>
            <span className={styles.accordionIcon}></span>
          </button>
          <div className={`${styles.accordionContent} ${openSections.charter ? styles.accordionContentOpen : ""}`}>
            <div className={styles.accordionInner}>
              <h3 className={styles.desktopSectionTitleCenter}>コミュニティ憲章</h3>
              <div className={styles.charterHeader}>
                <p className={styles.charterIntro}>
                  MetaCampUsは、「やりたい」を原動力に、想いをカタチにしていく共創のキャンパスです。
                  <br />
                  ここでは、誰もが安心して参加し、自由に語り、共に創るために、次の5つの約束を大切にしています。
                </p>
              </div>
              <div className={styles.charterGrid}>
                {charterItems.map((item, index) => (
                  <div key={item.number} className={styles.charterCard} style={{ animationDelay: `${index * 0.1}s` }}>
                    <div className={styles.charterNumber}>{item.number}</div>
                    <h4 className={styles.charterCardTitle}>{item.title}</h4>
                    <p className={styles.charterCardDescription}>{item.description}</p>
                  </div>
                ))}
              </div>
              <p className={styles.charterFooter}>
                MetaCampUsは、想いが循環する社会をつくるための小さな実験場です。
                <br />
                ここに集う一人ひとりの想いが、この町をかたちづくっていきます。
              </p>
            </div>
          </div>
        </div>

        {/* 3. コミュニティ参加のステップ */}
        <div ref={stepsRef} className={`${styles.accordionSection} ${stepsVisible ? styles.fadeInUp : styles.hidden}`}>
          <button
            className={`${styles.accordionHeader} ${openSections.steps ? styles.accordionOpen : ""}`}
            onClick={() => toggleSection("steps")}
          >
            <h3 className={styles.accordionTitle}>コミュニティ参加のステップ</h3>
            <span className={styles.accordionIcon}></span>
          </button>
          <div className={`${styles.accordionContent} ${openSections.steps ? styles.accordionContentOpen : ""}`}>
            <div className={styles.accordionInner}>
              <h3 className={styles.desktopSectionTitle}>コミュニティ参加のステップ</h3>
              <div className={styles.stepsContainer}>
                {steps.map((step, index) => (
                  <div key={step.step} className={styles.stepCard} style={{ animationDelay: `${index * 0.15}s` }}>
                    <div className={styles.stepHeader}>
                      <span className={styles.stepLabel}>STEP</span>
                      <span className={styles.stepNumber}>{step.step}</span>
                    </div>
                    <h4 className={styles.stepTitle}>{step.title}</h4>
                    <p className={styles.stepDescription}>{step.description}</p>
                    {step.buttonLink && (
                      <a
                        href={step.buttonLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${styles.stepButton} ${styles[step.buttonStyle]}`}
                      >
                        {step.buttonIcon && <img src={step.buttonIcon} alt="" className={styles.buttonIcon} />}
                        <span>{step.buttonText}</span>
                      </a>
                    )}
                    {step.shareButtons && (
                      <div className={styles.shareButtons}>
                        <a
                          href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
                            "MetaCampUs Japan（MCU：エムシーユー）は、新しい知と創造を育む、 オンライン上の共創コミュニティです。"
                          )}&url=${encodeURIComponent("https://metacampus.jp/")}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`${styles.shareButton} ${styles.shareButtonX}`}
                        >
                          <img src={XIcon} alt="X" className={styles.shareIcon} />
                          <span>Xでシェア</span>
                        </a>
                        <a
                          href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                            "https://metacampus.jp/"
                          )}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`${styles.shareButton} ${styles.shareButtonFacebook}`}
                        >
                          <img src={FacebookIcon} alt="Facebook" className={styles.shareIcon} />
                          <span>Facebookでシェア</span>
                        </a>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* 4. 活動イメージ */}
        <div
          ref={activityRef}
          className={`${styles.accordionSection} ${activityVisible ? styles.fadeInUp : styles.hidden}`}
        >
          <button
            className={`${styles.accordionHeader} ${openSections.activity ? styles.accordionOpen : ""}`}
            onClick={() => toggleSection("activity")}
          >
            <h3 className={styles.accordionTitle}>活動イメージ</h3>
            <span className={styles.accordionIcon}></span>
          </button>
          <div className={`${styles.accordionContent} ${openSections.activity ? styles.accordionContentOpen : ""}`}>
            <div className={styles.accordionInner}>
              <h3 className={styles.desktopSectionTitle}>活動イメージ</h3>
              <p className={styles.activityIntro}>
                MetaCampUsの中では、立場や専門を越えた交流が日常的に行われるように設計されています。
              </p>
              <ul className={styles.activityList}>
                {activities.map((activity, index) => (
                  <li key={index} className={styles.activityItem}>
                    {activity}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
